'use client';

import { useState, useEffect } from 'react';

import { logger } from '@/lib/logger';

import { ContentCard, ContentCardType, ContentCardVariant } from './';

import type { PersonalPlaylist } from '@/lib/api';
import type { Chapter } from '@/lib/contexts/media-controller/util';

export interface EnrichedContentCardProps {
  id: string | number;
  title: string;
  description?: string;
  imageUrl: string;
  contentType: ContentCardType;
  variant?: ContentCardVariant;
  className?: string;
  episodeIndex?: number;
  rawData?: { id: string; chapters: Chapter[] } | PersonalPlaylist;
  fetchPersonalPlaylistWithTracks?: (
    playlistId: string | number,
  ) => Promise<PersonalPlaylist | null>;
  shouldEnrich?: boolean;
  enrichCondition?: (id: string | number) => boolean;
}

export const EnrichedContentCard = ({
  id,
  title,
  description,
  imageUrl,
  contentType,
  variant = ContentCardVariant.DETAILED,
  className = 'w-full',
  episodeIndex,
  rawData,
  fetchPersonalPlaylistWithTracks,
  shouldEnrich = false,
  enrichCondition,
}: EnrichedContentCardProps) => {
  const [playlistData, setPlaylistData] = useState<PersonalPlaylist | null>(
    null,
  );

  useEffect(() => {
    const needsEnrichment =
      shouldEnrich || (enrichCondition && enrichCondition(id));

    if (needsEnrichment && fetchPersonalPlaylistWithTracks) {
      fetchPersonalPlaylistWithTracks(id)
        .then(data => {
          if (data) {
            setPlaylistData(data);
          }
        })
        .catch(err => logger.error(err));
    }
  }, [id, shouldEnrich, enrichCondition, fetchPersonalPlaylistWithTracks]);

  const finalRawData = playlistData || rawData;

  return (
    <ContentCard
      variant={variant}
      contentType={contentType}
      description={description}
      title={title}
      imageUrl={imageUrl}
      slug={String(id)}
      className={className}
      episodeIndex={episodeIndex}
      rawData={finalRawData}
    />
  );
};
